import { DEFAULT_FLOOR_PLAN } from '../../utils/floorPlanGeometry.logic';
import React from 'react';
import { Group, Rect, Text, Circle, Arrow, Line } from 'react-konva';
import { useEditorStore, RackNode, ToolType, PPM } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import { calculateBayingSnap } from '../../utils/cadSnapping.logic';
import { getRackCanvasVisuals } from '../../utils/rackLifecycle.logic';
import { validateStructuralPlacement } from '../../utils/structuralGeometry.logic';
import type { EditorCommandId } from '../../utils/editorCommands.logic';

// ─── Rack type styling ─────────────────────────────────────────────────────────
const RACK_COLORS: Record<string, string> = {
  server_rack:  '#1a2744',
  network_rack: '#0f2030',
  open_frame:   '#1a1f2a',
  crac:         '#2a1a3a',
  inrow_cooler: '#1a2035',
  ups:          '#3a2a1a',
  pdu_frame:    '#2a1e0f',
  pillar:       '#27272a',
};

const RACK_ACCENT: Record<string, string> = {
  server_rack:  '#3b82f6',
  network_rack: '#22d3ee',
  open_frame:   '#64748b',
  crac:         '#a855f7',
  inrow_cooler: '#818cf8',
  ups:          '#f97316',
  pdu_frame:    '#fb923c',
  pillar:       '#71717a',
};

const RACK_CODE: Record<string, string> = {
  server_rack:  'SRV',
  network_rack: 'NET',
  open_frame:   'FRM',
  crac:         'CRAC',
  inrow_cooler: 'IRC',
  ups:          'UPS',
  pdu_frame:    'PDU',
  pillar:       'COL',
};

// ─── Hot/cold face colors ──────────────────────────────────────────────────────
// Front face = cold (blue) — rack faces cold aisle with its ports/front
// Rear face  = hot (orange) — exhaust side
const COLD_COLOR = 'rgba(56,189,248,0.75)';
const HOT_COLOR  = 'rgba(249,115,22,0.65)';

// ─── Orientation → front face direction ───────────────────────────────────────
// orientation 0: front is at bottom (south) of rect in canvas space
// ─── RackShape ────────────────────────────────────────────────────────────────
interface RackShapeProps {
  rack: RackNode;
  allRacks: Record<string, RackNode>;
  isSelected: boolean;
  isHovered: boolean;
  isColliding: boolean;
  tool: ToolType;
  showDetail: boolean;
  onSelect: (additive: boolean) => void;
  onHover: (on: boolean) => void;
  onDragStart: () => void;
  onDragEnd: (x: number, y: number) => void;
  onContextMenu: (x: number, y: number) => void;
  onMeasurePoint?: (point: { x: number; y: number }, id: string) => void;
}

const RackShape: React.FC<RackShapeProps> = ({
  rack, allRacks, isSelected, isHovered, isColliding, tool, showDetail,
  onSelect, onHover, onDragStart, onDragEnd, onContextMenu, onMeasurePoint,
}) => {
  const isOther       = rack.tenant === 'other';
  const defaultAccent = RACK_ACCENT[rack.rackType] || '#3b82f6';
  const visuals       = getRackCanvasVisuals(rack, defaultAccent);
  const isReserved    = rack.tenant === 'reserved' || visuals.status === 'reserved';
  const isUnassigned = !rack.rowId || !Number.isInteger(rack.rackPosInRow) || rack.label === 'Без адреса';
  const baseFill      = RACK_COLORS[rack.rackType] || '#1a2744';
  const accent        = visuals.strokeColor || defaultAccent;

  const fill        = isOther ? '#374151' : visuals.status === 'reserved' ? 'rgba(168, 85, 247, 0.15)' : (isReserved ? 'transparent' : baseFill);
  const strokeWidth = isColliding ? 3 : (isSelected ? 2.5 : isHovered ? 1.5 : 1);
  const strokeColor = isColliding ? '#ef4444' : (isSelected ? '#ffffff' : isUnassigned ? '#f59e0b' : isHovered ? accent : (isOther ? '#4b5563' : (visuals.strokeColor || accent + '88')));
  const strokeDash  = isColliding ? [5, 5] : (isUnassigned ? [7, 4] : (visuals.strokeDash || (isReserved || rack.widthM == null || rack.depthM == null ? [4, 4] : undefined)));

  const w = rack.widthM != null ? rack.widthM * PPM : rack.width;
  const h = rack.depthM != null ? rack.depthM * PPM : rack.height;

  const isPillar   = rack.rackType === 'pillar' || rack.isBlocked;
  // Local coordinates in rack space (Group rotation handles 0/90/180/270 transform)
  const tFront = 4;
  const tRear = 3;
  const frontStrip = { x: 0, y: h - tFront, width: w, height: tFront };
  const rearStrip  = { x: 0, y: 0, width: w, height: tRear };
  const cx = w / 2, cy = h / 2;
  const d = Math.min(w, h) * 0.22;
  const [ax0, ay0, ax1, ay1] = [cx, cy - d / 2, cx, cy + d / 2];

  return (
    <Group
      x={rack.x + w / 2}
      y={rack.y + h / 2}
      offsetX={w / 2}
      offsetY={h / 2}
      rotation={rack.rotation || 0}
      opacity={visuals.opacity}
      draggable={tool === 'select' && !isOther}
      onClick={e => { e.cancelBubble = true; if (tool === 'measure') onMeasurePoint?.({ x: rack.x + w / 2, y: rack.y + h / 2 }, rack.id); else onSelect(e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey); }}
      onDblClick={e => { e.cancelBubble = true; }}
      onContextMenu={e => { e.evt.preventDefault(); e.cancelBubble = true; onContextMenu(e.evt.clientX, e.evt.clientY); }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onDragStart={onDragStart}
      onDragEnd={e => {
        const rawX = e.target.x() - w / 2;
        const rawY = e.target.y() - h / 2;
        const store = useEditorStore.getState();
        const floorPlan = store.dcSettings.floorPlan ?? DEFAULT_FLOOR_PLAN;
        const gridPx = floorPlan.tileSizeMm / 1000 * PPM;
        const selectedSet = new Set(store.selectedIds);
        const otherRects = Object.values(allRacks)
          .filter(r => r.id !== rack.id && (selectedSet.size <= 1 || !selectedSet.has(r.id)))
          .map(r => ({
            id: r.id,
            x: r.x,
            y: r.y,
            width: r.widthM != null ? r.widthM * PPM : r.width,
            height: r.depthM != null ? r.depthM * PPM : r.height,
            rotation: r.rotation || 0,
          }));
        const snap = calculateBayingSnap(
          { id: rack.id, x: rawX, y: rawY, width: w, height: h, rotation: rack.rotation || 0 },
          otherRects,
          15,
          gridPx,
          floorPlan.originXMm / 1000 * PPM,
          floorPlan.originYMm / 1000 * PPM,
        );
        const placementCheck = validateStructuralPlacement(
          { id: rack.id, x: snap.x, y: snap.y, w, h, rotation: rack.rotation || 0 },
          store.dcSettings.structuredGeometry,
          {
            widthM: floorPlan.widthMm / 1000,
            heightM: floorPlan.heightMm / 1000,
            originXM: floorPlan.originXMm / 1000,
            originYM: floorPlan.originYMm / 1000,
            ppm: PPM,
          },
        );
        if (!placementCheck.valid) {
          onDragEnd(rack.x, rack.y);
          e.target.position({ x: rack.x + w / 2, y: rack.y + h / 2 });
          return;
        }
        onDragEnd(snap.x, snap.y);
        e.target.position({ x: snap.x + w / 2, y: snap.y + h / 2 });
      }}
    >
      {/* Body */}
      <Rect
        width={w}
        height={h}
        fill={fill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        dash={strokeDash}
        cornerRadius={4}
        shadowColor={isColliding ? '#ef4444' : (isSelected ? accent : 'transparent')}
        shadowBlur={isColliding ? 15 : (isSelected ? 12 : 0)}
        shadowOpacity={0.8}
      />

      {/* Rear face (hot exhaust) — only for IT & cooling/power equipment, not pillars */}
      {!isPillar && (
        <Rect
          {...rearStrip}
          fill={HOT_COLOR}
          cornerRadius={2}
          listening={false}
          opacity={0.8}
        />
      )}

      {/* Front face (cold intake) */}
      {!isPillar && (
        <Rect
          {...frontStrip}
          fill={COLD_COLOR}
          cornerRadius={2}
          listening={false}
          opacity={0.9}
        />
      )}

      {/* Direction arrow — shown when zoomed in */}
      {!isPillar && showDetail && (
        <Arrow
          points={[ax0, ay0, ax1, ay1]}
          fill={COLD_COLOR}
          stroke={COLD_COLOR}
          strokeWidth={1.5}
          pointerLength={5}
          pointerWidth={5}
          listening={false}
          opacity={0.7}
        />
      )}

      {/* Striped hatch overlay for reserved racks (Ticket-05 NetBox alignment) */}
      {visuals.hasHatchOverlay && (
        <Group listening={false} clipFunc={(ctx: any) => { ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.closePath(); }}>
          {Array.from({ length: Math.ceil((w + h) / 10) + 1 }).map((_, i) => (
            <Line
              key={`hatch-${i}`}
              points={[-h + i * 10, h, i * 10, 0]}
              stroke="#a855f7"
              strokeWidth={1.5}
              opacity={0.35}
            />
          ))}
        </Group>
      )}

      {/* Dirty indicator */}
      {rack.isDirty && (
        <Circle x={w - 8} y={8} radius={4} fill={isUnassigned ? '#f59e0b' : '#facc15'} />
      )}

      {/* Deprecated / Reserved / Planned Status Badge */}
      {visuals.badgeStyle && (
        <Group x={w - (visuals.badgeStyle.text.length > 3 ? 32 : 26)} y={6} listening={false}>
          <Rect
            width={visuals.badgeStyle.text.length > 3 ? 28 : 22}
            height={11}
            fill={visuals.badgeStyle.bg}
            stroke={visuals.badgeStyle.border}
            strokeWidth={1}
            cornerRadius={2}
          />
          <Text
            text={visuals.badgeStyle.text}
            x={0}
            y={2}
            width={visuals.badgeStyle.text.length > 3 ? 28 : 22}
            align="center"
            fontSize={7}
            fontFamily="JetBrains Mono, monospace"
            fontStyle="bold"
            fill={visuals.badgeStyle.color}
          />
        </Group>
      )}

      {/* Icon + label — only when zoomed in */}
      {showDetail && (
        <Group
          x={w / 2}
          y={h / 2}
          offsetX={w / 2}
          offsetY={h / 2}
          rotation={-(rack.rotation || 0)}
        >
          <Text
            text={visuals.shortCode || RACK_CODE[rack.rackType] || 'SRV'}
            x={0}
            y={h / 2 - 14}
            width={w}
            align="center"
            fontSize={Math.max(8, Math.min(10, w * 0.24))}
            fontFamily="JetBrains Mono, monospace"
            fontStyle="bold"
            letterSpacing={1}
            fill={accent}
            listening={false}
          />
          <Text
            text={rack.label === 'Без адреса' ? 'Нет адреса' : rack.label}
            x={2}
            y={h - 17}
            width={w - 4}
            align="center"
            fontSize={Math.max(7, Math.min(9, w * 0.16))}
            fontFamily="JetBrains Mono, monospace"
            fill="#94a3b8"
            wrap="none"
            listening={false}
          />
          {isUnassigned && !rack.isFacility && !isPillar && rack.label !== 'Без адреса' && rack.label !== 'Адрес неизвестен' && (
            <Text text="Метка" x={2} y={h - 28} width={w - 4} align="center" fontSize={7}
              fontFamily="JetBrains Mono, monospace" fill="#f59e0b" listening={false} />
          )}
        </Group>
      )}

      {/* Selection handles */}
      {isSelected && (
        <>
          {[
            [0, 0], [w / 2, 0], [w, 0],
            [0, h / 2], [w, h / 2],
            [0, h], [w / 2, h], [w, h],
          ].map(([hx, hy], i) => (
            <Rect
              key={i}
              x={(hx as number) - 4}
              y={(hy as number) - 4}
              width={8}
              height={8}
              fill="#ffffff"
              stroke={accent}
              strokeWidth={1.5}
              cornerRadius={1}
              listening={false}
            />
          ))}
        </>
      )}
    </Group>
  );
};

// ─── RacksLayer ───────────────────────────────────────────────────────────────
export const RacksLayer: React.FC<{
  onContextMenu?: (rackId: string, x: number, y: number) => void;
  executeCommand?: (id: EditorCommandId) => void;
  onMeasurePoint?: (point: { x: number; y: number }, id: string) => void;
}> = ({ onContextMenu, executeCommand, onMeasurePoint }) => {
  const {
    racks,
    selectedIds, toggleSelected, setSelectedIds,
    hoveredId, setHoveredId,
    tool,
    showLabels,
    stageScale,
    stageX, stageY, stageW, stageH,
    moveRacks,
    deleteRack,
    collidingIds,
  } = useEditorStore(useShallow(state => ({
    racks: state.racks, selectedIds: state.selectedIds, toggleSelected: state.toggleSelected,
    setSelectedIds: state.setSelectedIds, hoveredId: state.hoveredId, setHoveredId: state.setHoveredId,
    tool: state.tool, stageScale: state.stageScale, moveRacks: state.moveRacks, deleteRack: state.deleteRack,
    showLabels: state.showLabels,
    collidingIds: state.collidingIds,
    stageX: state.stageX, stageY: state.stageY, stageW: state.stageW, stageH: state.stageH,
  })));

  const showDetail = stageScale > 0.4;

  // Viewport Culling (Ticket 04)
  const scale = Math.max(0.01, stageScale);
  const viewLeft = -stageX / scale;
  const viewTop = -stageY / scale;
  const viewRight = viewLeft + stageW / scale;
  const viewBottom = viewTop + stageH / scale;
  const CULL_MARGIN = 150; // extra margin in px to prevent popping during panning

  return (
    <Group>
      {Object.values(racks).map(rack => {
        const rw = rack.widthM != null ? rack.widthM * PPM : rack.width;
        const rh = rack.depthM != null ? rack.depthM * PPM : rack.height;
        const maxDim = Math.max(rw, rh);
        const isVisible = (
          rack.x + maxDim >= viewLeft - CULL_MARGIN &&
          rack.x <= viewRight + CULL_MARGIN &&
          rack.y + maxDim >= viewTop - CULL_MARGIN &&
          rack.y <= viewBottom + CULL_MARGIN
        );

        if (!isVisible && !selectedIds.includes(rack.id)) {
          return null;
        }

        return (
          <RackShape
            key={rack.id}
            rack={rack}
            allRacks={racks}
            isSelected={selectedIds.includes(rack.id)}
            isHovered={hoveredId === rack.id}
            isColliding={collidingIds.includes(rack.id)}
            tool={tool}
            showDetail={showDetail && showLabels}
            onSelect={additive => {
              if (tool === 'erase') {
                if (executeCommand) {
                  setSelectedIds([rack.id]);
                  executeCommand('selection.delete');
                } else deleteRack(rack.id);
                return;
              }
              toggleSelected(rack.id, additive);
            }}
            onHover={on => setHoveredId(on ? rack.id : null)}
            onDragStart={() => {
              if (!selectedIds.includes(rack.id)) setSelectedIds([rack.id]);
            }}
            onDragEnd={(x, y) => {
              const dx = x - rack.x;
              const dy = y - rack.y;
              const store = useEditorStore.getState();
              const ids = store.selectedIds.includes(rack.id)
                ? store.selectedIds
                : [rack.id];
              const moves = ids
                .map(id => {
                  const r = store.racks[id];
                  return r ? { id, x: r.x + dx, y: r.y + dy } : null;
                })
                .filter(Boolean) as { id: string; x: number; y: number }[];
              moveRacks(moves);
            }}
            onContextMenu={(x, y) => onContextMenu?.(rack.id, x, y)}
            onMeasurePoint={onMeasurePoint}
          />
        );
      })}
    </Group>
  );
};
